import type { Marketing } from '@motorcity/shared';
import { Select, SectionHeader, Switch } from './ui/primitives';

const CATEGORIES = ['SUV', 'Sedan', 'Hatchback', 'Pickup', 'Van', 'Truck', 'Bus', 'Coupe'];

/**
 * Classification and outbound links.
 *
 * These exist because `/mobapp/v1.json` reproduces the legacy `VehicleData.json`
 * feed key-for-key, and that feed carried them. They are genuine content — the
 * app deep-links to these URLs and filters on category — so editors own them
 * here rather than them being frozen at whatever the import found.
 */
export function MarketingFields({
  value,
  onChange,
}: {
  value: Marketing;
  onChange: (next: Marketing) => void;
}) {
  const set = <K extends keyof Marketing>(key: K, next: Marketing[K]) =>
    onChange({ ...value, [key]: next });

  return (
    <section className="card">
      <SectionHeader
        title="Classification & links"
        description="Feeds the app's filters and its “request a quote” and “book a test drive” buttons."
      />

      <div className="grid-2">
        <div className="field">
          <span className="field-label">Vehicle type</span>
          <Select
            value={value.vehicleType}
            ariaLabel="Vehicle type"
            onChange={(next) => set('vehicleType', next as Marketing['vehicleType'])}
          >
            <option value="Passenger">Passenger</option>
            <option value="Commercial">Commercial</option>
          </Select>
        </div>

        <label className="field">
          <span className="field-label">
            Category <span className="required">*</span>
          </span>
          <input
            className="input"
            list="model-categories"
            placeholder="SUV"
            value={value.category}
            onChange={(e) => set('category', e.target.value)}
          />
          <datalist id="model-categories">
            {CATEGORIES.map((c) => (
              <option key={c} value={c} />
            ))}
          </datalist>
          <span className="field-hint">
            Required to publish — the app groups and filters by this, so a model with no category
            drops out of every listing.
          </span>
        </label>
      </div>

      <div className="field">
        <Switch
          checked={value.featured}
          onChange={(next) => set('featured', next)}
          label="Featured"
          hint="Highlights this model on the app's home screen."
        />
      </div>

      <div className="field">
        <Switch
          checked={value.testDriveAvailable}
          onChange={(next) => set('testDriveAvailable', next)}
          label="Test drive available"
          hint="Controls whether the app shows the test-drive button at all."
        />
      </div>

      <UrlField
        label="Model page"
        hint="The brand's own page for this model."
        value={value.externalLink}
        onChange={(next) => set('externalLink', next)}
      />
      <UrlField
        label="Request a quote"
        value={value.quoteUrl}
        onChange={(next) => set('quoteUrl', next)}
      />
      <UrlField
        label="Book a test drive"
        value={value.testDriveUrl}
        onChange={(next) => set('testDriveUrl', next)}
      />

      <label className="field">
        <span className="field-label">Legacy feed id</span>
        <input
          className="input mono"
          value={value.feedId}
          placeholder="auto"
          onChange={(e) => set('feedId', e.target.value.trim())}
        />
        <span className="field-hint">
          The <code>nid</code> this model appears under in <code>/mobapp/v1.json</code>. Preserved
          from the old feed so existing app builds keep resolving it — leave blank for automatic.
        </span>
      </label>
    </section>
  );
}

function UrlField({
  label,
  hint,
  value,
  onChange,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
}) {
  const invalid = value !== '' && !/^https?:\/\//i.test(value);
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      <input
        className="input"
        type="url"
        inputMode="url"
        placeholder="https://"
        value={value}
        aria-invalid={invalid || undefined}
        onChange={(e) => onChange(e.target.value.trim())}
      />
      {invalid ? (
        <span className="field-error">Must start with http:// or https://</span>
      ) : hint ? (
        <span className="field-hint">{hint}</span>
      ) : null}
    </label>
  );
}
